import { pulse, seeded, seg, tween } from '../../../core/effect/motion';
import {
  CARD_ASPECT, add, card, fade, place, playerColor,
} from './common';

/**
 * **ダメージ計算（ぶつかる瞬間）**（Issue #553。指示書 `../direction/battle.md`）。
 *
 * マスターデュエルのように**攻撃するカードが盤から浮いて相手のカードへぶつかり**、ぶつかった瞬間に**画面を切る**。
 *
 * ```
 * 0     浮き上がる: 攻撃するカードが盤から浮き、少し大きくなって影が落ちる（**盤のカードそのものを借りて動かす**。R13）
 * 150   溜め: 少し後ろへ引きながら対象の方へ向く（攻撃宣言の矢印の向き）
 * 450   突進: 残像と硬い線を引いて、矢印の上をなぞって対象へ一直線
 * 600   衝突 → 画面を切る: 衝突点を通る斜めの白い一線が走り、線を境に画面の上と下が逆向きにずれる（scene.split）。止め 120ms
 * 720   ずれが戻りながら揺れ（scene.shake）・硬い火花・鋼の破片。対象は弾かれて傾く
 * 1100  攻撃するカードは跳ね返って元の場所へ戻り、1560 で盤のカードに返す
 * ```
 *
 * **カードは描かない**（盤のカードを `tweaks` で動かす。影も盤のカードが持っているもの）。演出の板は残像だけ。
 * 演出の層は盤のカードの層より必ず手前に重なる（別の canvas）ので、残像は**カードに重ならないところまで離れたものだけ**出す。裏守備の対象は、ダメージ計算の前に
 * core が `pos_change`（裏守備 → 表守備）を出して表にしている（`pos_change.js` が動きだけで見せる）ので、ここで返すことは無い。
 *
 * 直接攻撃は相手の陣地の奥（攻撃するカードの正面の列の、魔法・罠の列のさらに奥＝盤の外）へ突っ込んで、同じように画面を切る。
 * 破壊（`move`）と戦闘ダメージの数字（`damage`）は続く別の演出。ここは「ぶつかって斬った」まで。
 * もやもやした光は使わない（閃光は scene の grade と形のある板、火花は硬い三角）。
 */
const D = 1600;
const LIFT = 150;
const CHARGE = 450;
const HIT = 600;
const STOP = 120;
const RECOIL = HIT + STOP;
const BACK = 1100;
const LAND = 1500;
const HOME = 1560;
const GHOSTS = 4;

/** 浮く高さ（ワールドの単位＝平面のピクセル。カードの幅に対する比） */
const LIFT_K = 0.55;
/** 直接攻撃が突っ込む先（正面の相手のモンスターの枠 → 魔法・罠の枠 を何倍伸ばすか。1 を超えると盤の外） */
const DIRECT_T = 2.1;
/** 画面を切る線の傾き（度。舞台のピクセルで y 下向き） */
const CUT_ANGLE = -26;
/** ずれの大きさ（px。線に沿って両側が逆向きに）と、切れ目の幅 */
const CUT_OFFSET = 120;
const CUT_GAP = 22;

const oppOf = (payload) => (payload?.player === 1 ? 0 : 1);
/** 攻撃するカードの正面の相手の列（P2 は原点まわりに 180° 回っているので並びが逆＝ 6 - seq） */
const frontSeq = (zone) => {
  const m = /^monster:[01]:([1-5])$/.exec(String(zone ?? ''));
  return m ? 6 - Number(m[1]) : 3;
};
/** 直接攻撃の突っ込む先（2 つの枠の間の外）。scene の切る線と、カードの行き先が同じ点になる */
const directAt = (payload) => {
  const s = frontSeq(payload?.attacker?.zone);
  const o = oppOf(payload);
  return { from: { zone: `monster:${o}:${s}` }, to: { zone: `magic_trap:${o}:${s}` }, t: DIRECT_T };
};
/** 切る線が通る点（対象のカード / 直接攻撃は突っ込む先） */
const cutAt = (payload, direct) => (direct
  ? directAt(payload)
  : { card: payload.target.uid, else: { zone: payload.target.zone ?? '' } });

/** ヒットストップの時間の写し（止めの間は HIT で止まる） */
const hs = (t) => (t < HIT ? t : (t < RECOIL ? HIT : t - STOP));
const wrap = (a) => Math.atan2(Math.sin(a), Math.cos(a));
/** 盤の上の向き → カードの絵の上が向く方向（ワールドの x / z）。`rotation.y = r` で (0,0,-1) が向く先 */
const facing = (r) => [-Math.sin(r), -Math.cos(r)];
/** 平面の向き（x, z）の角度 */
const phi = (v) => Math.atan2(v[1], v[0]);

/**
 * **動きの幾何**（盤の層と画面の層が同じものを使う）。平面の座標（ワールドは平面の中心が原点）で出す。
 * 無ければ null（写らない試合では何も出さない）。
 */
function geometry(ctx, direct) {
  const { payload } = ctx;
  const f = ctx.stage?.field;
  const unit = f?.unit > 0 ? f.unit : 0;
  const plane = f?.plane ?? null;
  const from = ctx.anchor?.({ card: payload.attacker?.uid ?? '', else: { zone: payload.attacker?.zone ?? '' } });
  if (!from?.plane || !(unit > 0) || !plane) return null;
  const board = ctx.board;
  const aCard = board?.byUid?.(payload.attacker.uid)?.card ?? null;
  const cw = unit;
  const ch = unit / CARD_ASPECT;
  const baseA = num(f.cardAngle?.(from.zone)) + (aCard?.defense ? Math.PI / 2 : 0);
  let to = null;
  let baseT = 0;
  let halfT = 0;
  if (!direct) {
    const a = ctx.anchor({ card: payload.target.uid, else: { zone: payload.target.zone ?? '' } });
    if (!a?.plane) return null;
    to = a.plane;
    const tCard = board?.byUid?.(payload.target.uid)?.card ?? null;
    baseT = num(f.cardAngle?.(a.zone)) + (tCard?.defense ? Math.PI / 2 : 0);
  } else {
    const at = directAt(payload);
    const m = ctx.anchor(at.from);
    const s = ctx.anchor(at.to);
    if (!m?.plane || !s?.plane) return null;
    to = [m.plane[0] + (s.plane[0] - m.plane[0]) * at.t, m.plane[1] + (s.plane[1] - m.plane[1]) * at.t];
  }
  const dx = to[0] - from.plane[0];
  const dz = to[1] - from.plane[1];
  const L = Math.hypot(dx, dz);
  if (!(L > 1)) return null;
  const dirV = [dx / L, dz / L];
  if (!direct) {
    // 対象の板の、突進の向きに沿った半分の長さ（守備表示なら横向き）
    const ax = [Math.cos(baseT), -Math.sin(baseT)];
    const az = [Math.sin(baseT), Math.cos(baseT)];
    halfT = (cw / 2) * Math.abs(dirV[0] * ax[0] + dirV[1] * ax[1]) + (ch / 2) * Math.abs(dirV[0] * az[0] + dirV[1] * az[1]);
  }
  // 攻撃するカードの中心が止まる距離（前の縁が対象の縁に少し食い込む）
  const stop = direct ? L : Math.max(ch * 0.3, L - halfT - ch / 2 + ch * 0.08);
  const turn = wrap(phi(facing(baseA)) - phi(dirV));
  return {
    cw, ch, plane, from: from.plane, to, dirV, L, stop, baseA, baseT, turn, aCard,
    contact: direct ? to : [to[0] - dirV[0] * halfT, to[1] - dirV[1] * halfT],
  };
}

const num = (v, def = 0) => (Number.isFinite(v) ? v : def);

/**
 * **攻撃するカードの置き方**（平面の座標 `[x, z]`・高さ・向き・倍率）。時刻 `t`（帯のローカル）だけで決まる。
 */
function attackerAt(g, t) {
  const u = hs(t);
  const liftK = tween(t, 0, LIFT, 'back_out') * (1 - tween(t, LAND - 40, HOME, 'power2_in'));
  const face = tween(t, LIFT, CHARGE - 60, 'ease_in_out') * (1 - tween(t, BACK + 80, LAND, 'ease_in_out'));
  // 溜め（後ろへ引く）→ 突進 → 当たって少し跳ね返る → 戻る
  const pull = tween(t, LIFT, CHARGE, 'ease_out') * (1 - seg(t, CHARGE, CHARGE + 60));
  const dash = tween(t, CHARGE, HIT, 'power3_in');
  const bounce = 0.16 * Math.sin(seg(u, HIT, HIT + 180) * Math.PI * 0.5);
  const ret = tween(t, BACK, LAND, 'ease_in_out');
  let d = -g.ch * 0.28 * pull + g.stop * dash;
  if (t >= HIT) d = g.stop - g.ch * bounce;
  d *= 1 - ret;
  const x = g.from[0] + g.dirV[0] * d;
  const z = g.from[1] + g.dirV[1] * d;
  // 戻りは少し弧を描く
  const arc = Math.sin(ret * Math.PI) * g.cw * 0.35;
  const y = g.cw * LIFT_K * liftK + arc;
  // 当たった瞬間に進む向きに潰れる
  const squash = t >= HIT ? 0.2 * pulse(u, { at: HIT, inMs: 10, hold: 0, outMs: 200 }) : 0;
  return {
    x, y, z, rot: g.baseA - g.turn * face, s: 1 + 0.1 * liftK, squash,
  };
}

/**
 * **対象の置き方**（当たったら弾かれて傾く → 元の場所へ落ち着く）。盤の元の位置からの差で返す。
 * 傾き `tilt` は板の `rotation.z`（器の `place` が素に戻すもの）に当てる。
 */
function targetAt(g, t, lean) {
  const u = hs(t);
  const knock = t >= HIT ? Math.sin(seg(u, HIT, HIT + 260) * Math.PI * 0.5) * (1 - tween(u, HIT + 380, BACK + 200, 'ease_in_out')) : 0;
  const lift = g.cw * 0.12 * tween(t, 0, LIFT, 'ease_out') * (1 - tween(t, LAND - 40, HOME, 'power2_in')) + g.cw * 0.35 * knock;
  const push = g.ch * 0.34 * knock;
  return {
    dx: g.dirV[0] * push, dz: g.dirV[1] * push, y: lift, dRot: lean * knock, tilt: 0.22 * knock,
  };
}

/** 対象の傾きの向き（帯ごとに決まる） */
const leanOf = (seed) => (seeded(`${seed}:battle`)() < 0.5 ? -1 : 1) * 0.32;

/** ダメージ計算の組み立て。`direct` = 直接攻撃（相手の陣地の奥へ突っ込む） */
export const battleSpec = (direct) => ({
  label: 'ダメージ計算（ぶつかる瞬間）',
  color: '#ff3b3b',
  duration: () => D,
  /*
   * **画面を切る**（ぶつかった瞬間）。線の両側が逆向きに滑って止まり（ヒットストップ）、弾んで戻る。
   * 閃光は grade の 2 フレーム（画面全体の加算の白は WebKit の録画で実写の色を壊すので使わない）。
   */
  scene: ({ payload }) => (payload?.attacker?.uid ? [
    {
      ms: HIT,
      split: {
        at: cutAt(payload, direct),
        angle: payload.player === 1 ? -CUT_ANGLE : CUT_ANGLE,
        offset: {
          keys: [
            { ms: 0, value: 0 },
            { ms: 30, value: 0, ease: 'expo_out' },
            { ms: 80, value: CUT_OFFSET },
            { ms: STOP, value: CUT_OFFSET, ease: 'elastic_out' },
            { ms: STOP + 420, value: 0 },
          ],
        },
        gap: {
          keys: [
            { ms: 0, value: 0 },
            { ms: 30, value: 0, ease: 'expo_out' },
            { ms: 80, value: CUT_GAP },
            { ms: STOP, value: CUT_GAP, ease: 'ease_in' },
            { ms: STOP + 180, value: 0 },
          ],
        },
      },
      grade: {
        contrast: { keys: [{ ms: 0, value: 1.4 }, { ms: 50, value: 1.4, ease: 'hold' }, { ms: 51, value: 1.12 }, { ms: STOP, value: 1.15, ease: 'ease_out' }, { ms: STOP + 200, value: 1 }] },
        brightness: { keys: [{ ms: 0, value: 1.18 }, { ms: 34, value: 1.18, ease: 'hold' }, { ms: 35, value: 1 }] },
        chroma: { keys: [{ ms: 0, value: 0.012 }, { ms: STOP + 240, value: 0, ease: 'ease_out' }] },
      },
    },
    { ms: RECOIL, shake: { amp: 34, freq: 26, decayMs: 440 } },
  ] : []),
  sounds: () => [
    { at: HIT - 20, name: 'attack' },
    { at: LAND, name: 'set_card', gain: 0.5 },
  ],
  /*
   * **盤のカードを借りて動かす**（攻撃するカードと対象。handle は `stage/three/runtime.js::handleFor`）。
   * 毎フレーム素の置き方から組み（`h.reset()`）、盤の元の位置・向きからの差だけ足す。
   * 触るのは器が素に戻すもの（root の位置・向き（y / z）・倍率、stack の位置）だけ —— 帯の外へシークしても崩れたまま残らない。
   * 帯の終わり（HOME）で何も返さない → 盤に戻る。
   */
  tweaks: (ctx) => {
    const { payload, localMs: t } = ctx;
    if (t >= HOME || !payload?.attacker?.uid) return {};
    const g = geometry(ctx, direct);
    if (!g) return {};
    const out = {
      [payload.attacker.uid]: (h) => {
        h.reset?.();
        const a = attackerAt(g, t);
        const stack = h.stack ?? h.root;
        h.root.position.x += a.x - g.from[0];
        h.root.position.z += a.z - g.from[1];
        h.root.rotation.y += a.rot - g.baseA;
        h.root.scale.x *= a.s * (1 + a.squash * 0.5);
        h.root.scale.y *= a.s;
        h.root.scale.z *= a.s * (1 - a.squash);
        stack.position.y += a.y;
      },
    };
    if (!direct) {
      const lean = leanOf(ctx.seed);
      out[payload.target.uid] = (h) => {
        h.reset?.();
        const o = targetAt(g, t, lean);
        const stack = h.stack ?? h.root;
        h.root.position.x += o.dx;
        h.root.position.z += o.dz;
        h.root.rotation.y += o.dRot;
        h.root.rotation.z += o.tilt;
        stack.position.y += o.y;
      };
    }
    return out;
  },
  // 直接攻撃は相手の LP の枠が揺れる
  hud: ({ payload, localMs }) => (direct && payload?.attacker?.uid
    ? [{ anchor: 'life', player: oppOf(payload), shake: pulse(localMs, { at: RECOIL, inMs: 10, hold: 80, outMs: 380 }), phase: localMs }]
    : []),

  layers: [
    {
      id: 'cards',
      label: '突進の残像（カードそのものは盤のカードを借りて動かす）',
      space: 'board',
      script: (ctx) => {
        const { THREE, kit } = ctx;
        const root = new THREE.Group();
        const { payload } = ctx;
        const g = payload?.attacker?.uid ? geometry(ctx, direct) : null;
        if (!g) return { root };
        const col = playerColor(ctx, payload.player);
        const ghosts = Array.from({ length: GHOSTS }, (_, i) => {
          const m = kit.onBoard(card(ctx, { code: g.aCard?.code ?? null, player: payload.player, faceUp: g.aCard ? Boolean(g.aCard.faceUp) : true, w: g.cw }).mesh);
          m.material.color?.set?.(col);
          m.userData.alpha = 0.5 - i * 0.1;
          return add(root, m, 15 - i);
        });
        const toW = ([x, z], y = 0) => [x - g.plane.w / 2, y, z - g.plane.h / 2];

        return {
          root,
          update: ({ localMs: t }) => {
            const on = t < HOME;
            const a = attackerAt(g, t);
            // --- 残像（突進と戻りの間だけ）。カードに重なる近さのものは出さない（盤のカードより手前に描かれてしまう）
            const moving = seg(t, CHARGE, CHARGE + 40) * (1 - seg(t, HIT, HIT + 60))
              + 0.6 * seg(t, BACK, BACK + 60) * (1 - seg(t, LAND - 120, LAND));
            ghosts.forEach((m, i) => {
              const p = attackerAt(g, Math.max(0, t - (i + 1) * 22));
              const [gx, , gz] = toW([p.x, p.z]);
              m.position.set(gx, p.y - 1 - i, gz);
              m.rotation.y = p.rot;
              m.scale.set(p.s, 1, p.s);
              const apart = seg(Math.hypot(p.x - a.x, p.z - a.z), g.ch * 0.9, g.ch * 1.3);
              fade(m, on ? m.userData.alpha * moving * apart : 0);
            });
          },
        };
      },
    },
    {
      id: 'impact',
      label: '切る線・硬い火花・鋼の破片・突進の線',
      space: 'screen',
      script: (ctx) => {
        const { THREE } = ctx;
        const root = new THREE.Group();
        const { payload } = ctx;
        const g = payload?.attacker?.uid ? geometry(ctx, direct) : null;
        const toStage = (p) => ctx.stage?.field?.toStage?.(p) ?? null;
        if (!g) return { root };
        // 切る線の点は scene の split と同じ解き方（対象のカードの中心 / 直接攻撃は突っ込む先）
        const cut = direct ? toStage(g.to) : (ctx.anchor(cutAt(payload, direct))?.screen ?? toStage(g.to));
        const hit = toStage(g.contact);
        const from = toStage(g.from);
        if (!cut || !hit || !from) return { root };
        const rand = seeded(`${ctx.seed}:impact`);
        const cutRad = ((payload.player === 1 ? -CUT_ANGLE : CUT_ANGLE) * Math.PI) / 180;
        const dash = Math.atan2(hit[1] - from[1], hit[0] - from[0]);
        const plain = (color) => {
          const m = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), ctx.kit.plain(null, 1));
          m.material.color?.set?.(color);
          return m;
        };
        const tri = (color) => {
          const m = new THREE.Mesh(new THREE.CircleGeometry(1, 3), ctx.kit.plain(null, 1));
          m.material.color?.set?.(color);
          return m;
        };

        // 突進の硬い線（カードの後ろ。3 本）
        const speed = [-0.5, 0, 0.5].map((k) => ({ m: add(root, plain(k === 0 ? '#ffffff' : '#ffd21a'), 2), k }));
        // 切る線（白い一線。線より長く伸ばして、ずれても端が見えないようにする）と、芯の赤
        const line = add(root, plain('#ffffff'), 10);
        const core = add(root, plain('#ff2a2a'), 11);
        // 衝突点の硬い閃光（細長い菱形を 2 本交差）
        const star = [0, 1].map((i) => add(root, tri('#ffffff'), 12 + i * 0.01));
        // 火花（硬い三角。前へ扇に散る）
        const sparkCols = ['#ffd21a', '#fff3b0', '#ffb020', '#ffffff'];
        const sparks = Array.from({ length: 30 }, (_, i) => {
          const big = i < 10;
          const a = dash + (rand() - 0.5) * Math.PI * 1.5;
          return {
            m: add(root, tri(sparkCols[i % sparkCols.length]), 13),
            a,
            len: big ? 120 + rand() * 90 : 40 + rand() * 60,
            w: big ? 13 + rand() * 7 : 5 + rand() * 6,
            speed: 900 + rand() * 1300,
            r0: 10 + rand() * 30,
            life: 200 + rand() * 180,
          };
        });
        // 鋼の破片（暗いガンメタルと明るい縁。回りながら落ちる）
        const steel = ['#3a3f48', '#5b626e', '#9aa3b0', '#2a2e35'];
        const shards = Array.from({ length: 16 }, (_, i) => {
          const a = dash + (rand() - 0.5) * Math.PI * 1.7;
          return {
            m: add(root, tri(steel[i % steel.length]), 12),
            a,
            size: 10 + rand() * 18,
            stretch: 0.5 + rand() * 0.9,
            speed: 500 + rand() * 700,
            spin: (rand() - 0.5) * 24,
            rot0: rand() * Math.PI * 2,
            life: 460 + rand() * 260,
          };
        });
        const GRAV = 2600;

        return {
          root,
          update: ({ localMs: t }) => {
            const u = hs(t);
            // --- 突進の線: カードの後ろに伸び、当たったら縮む
            const a = attackerAt(g, t);
            const at = toStage([a.x, a.z]) ?? from;
            const len = t < HIT ? 420 * seg(t, CHARGE + 40, HIT) : 420 * (1 - seg(u, HIT, HIT + 70));
            speed.forEach(({ m, k }) => {
              const back = g.ch * 0.4;
              const ox = -Math.sin(dash) * k * 34; const oy = Math.cos(dash) * k * 34;
              place(m, at[0] - Math.cos(dash) * (back + len / 2) + ox, at[1] - Math.sin(dash) * (back + len / 2) + oy, {
                sx: Math.max(0.001, len * (k === 0 ? 1 : 0.7)), sy: k === 0 ? 6 : 3, rot: dash,
              });
              fade(m, t >= CHARGE && len > 1 ? 0.9 : 0);
            });

            // --- 切る線: 衝突点から両へ一瞬で走る → 止めの間は太く → 細くなって消える
            const grow = tween(t, HIT, HIT + 40, 'expo_out');
            const thin = 1 - tween(t, RECOIL, RECOIL + 220, 'power2_in');
            const L = 2800 * grow;
            place(line, cut[0], cut[1], { sx: Math.max(0.001, L), sy: 14 * thin, rot: cutRad });
            fade(line, t >= HIT && thin > 0.01 ? 1 : 0);
            place(core, cut[0], cut[1], { sx: Math.max(0.001, L), sy: 4 * thin, rot: cutRad });
            fade(core, t >= HIT && thin > 0.01 ? 1 : 0);

            // --- 衝突点の閃光（2 フレーム強）
            const fl = t >= HIT && t < HIT + 70 ? 1 - seg(t, HIT + 30, HIT + 70) : 0;
            star.forEach((m, i) => {
              place(m, hit[0], hit[1], { sx: 260 * (0.6 + 0.4 * fl), sy: 22, rot: dash + (i ? Math.PI / 2 : 0) });
              fade(m, fl);
            });

            // --- 火花: 当たった瞬間に出て、止めの間は止まり、そのあと飛ぶ
            const since = Math.max(0, u - HIT);
            sparks.forEach((s) => {
              const k = since / s.life;
              if (t < HIT || k >= 1) { fade(s.m, 0); return; }
              const r = s.r0 + s.speed * (since / 1000) * (1 - 0.45 * k);
              const l = s.len * (1 - k) * (t < RECOIL ? 0.6 : 1);
              place(s.m, hit[0] + Math.cos(s.a) * (r + l / 2), hit[1] + Math.sin(s.a) * (r + l / 2), {
                sx: Math.max(0.001, l), sy: s.w * (1 - 0.5 * k), rot: s.a,
              });
              fade(s.m, 1);
            });
            // --- 鋼の破片: 止めが明けてから飛び散り、落ちる
            const sinceS = Math.max(0, t - RECOIL);
            shards.forEach((s) => {
              const k = sinceS / s.life;
              if (t < RECOIL || k >= 1) { fade(s.m, 0); return; }
              const sec = sinceS / 1000;
              const x = hit[0] + Math.cos(s.a) * s.speed * sec;
              const y = hit[1] + Math.sin(s.a) * s.speed * sec + 0.5 * GRAV * sec * sec;
              place(s.m, x, y, { sx: s.size, sy: s.size * s.stretch, rot: s.rot0 + s.spin * sec });
              fade(s.m, 1 - seg(k, 0.7, 1));
            });
          },
        };
      },
    },
  ],
});
